import React, { ReactNode, useId, useState, JSX } from 'react'
import {
  NativeSyntheticEvent,
  TextInput,
  TextInputFocusEventData,
  TextInputProps,
  TextStyle,
  TouchableOpacityProps,
  View,
  ViewStyle
} from 'react-native'

import InformationIcon from '@common/assets/svg/InformationIcon'
import Text, { TextAppearance } from '@common/components/Text'
import { isMobileWebApp, isWeb } from '@common/config/env'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'
import { THEME_TYPES } from '@common/styles/themeConfig'
import useHover, { AnimatedPressable } from '@web/hooks/useHover'

import getStyles from './styles'

export interface InputProps extends TextInputProps {
  info?: string | boolean
  // Error message - Active if there is some error message string passed
  error?: string | boolean
  errorType?: 'error' | 'warning'
  label?: string
  isValid?: boolean
  validLabel?: string
  validLabelAppearance?: TextAppearance
  button?: string | JSX.Element | null
  buttonProps?: TouchableOpacityProps & {
    withBackground?: boolean
  }
  buttonStyle?: ViewStyle
  onButtonPress?: () => void
  disabled?: boolean
  containerStyle?: ViewStyle | ViewStyle[]
  inputStyle?: ViewStyle | ViewStyle[]
  setInputRef?: (ref: TextInput | null) => void
  inputBorderWrapperRef?: React.RefObject<View>
  nativeInputStyle?: ViewStyle & TextStyle
  borderWrapperStyle?: ViewStyle
  inputWrapperStyle?: ViewStyle | ViewStyle[]
  bottomLabelStyle?: TextStyle | TextStyle[]
  leftIcon?: () => ReactNode
  leftIconStyle?: ViewStyle
  tooltip?: {
    id: string
    content: string
  }
  childrenBeforeButtons?: React.ReactNode
  childrenBelowInput?: React.ReactNode
  borderless?: boolean
  customInputContent?: React.ReactNode
  renderConfirmAddress?: () => React.ReactNode
  preventJumpOnValidationChange?: boolean
}

const Input = ({
  label,
  button,
  buttonProps,
  buttonStyle,
  info,
  error,
  errorType,
  isValid,
  validLabel,
  validLabelAppearance,
  onBlur = () => {},
  onFocus = () => {},
  onButtonPress = () => {},
  disabled,
  containerStyle,
  inputStyle,
  nativeInputStyle,
  borderWrapperStyle,
  inputWrapperStyle,
  bottomLabelStyle,
  leftIcon,
  leftIconStyle,
  childrenBeforeButtons,
  childrenBelowInput,
  tooltip,
  borderless,
  setInputRef,
  inputBorderWrapperRef,
  customInputContent,
  editable,
  renderConfirmAddress,
  preventJumpOnValidationChange,
  ...rest
}: InputProps) => {
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const { theme, styles, themeType } = useTheme(getStyles)
  // Ties the visible <Text> label and the error/valid message to the field, so a
  // screen reader announces "<label>, edit" instead of a nameless "edit, blank",
  // and reads the validation message as soon as it appears.
  const inputId = useId()
  const bottomLabelId = `${inputId}-msg`
  const bottomLabelText = error || (isValid ? validLabel : undefined)
  const a11yProps = isWeb
    ? {
        'aria-describedby': bottomLabelText ? bottomLabelId : undefined,
        'aria-invalid': !!error
      }
    : {}
  const [bindAnim, animStyle] = useHover({ preset: 'opacityInverted' })

  const handleOnFocus = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
    if (disabled) return
    setIsFocused(true)
    return onFocus(e)
  }
  const handleOnBlur = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
    if (disabled) return
    setIsFocused(false)
    return onBlur(e)
  }

  const hasButton = !!button

  const borderWrapperStyles = [
    styles.borderWrapper,
    isFocused &&
      !isMobileWebApp && {
        borderColor: themeType === THEME_TYPES.DARK ? `${theme.primary as string}35` : theme.primary
      },
    isValid && !isMobileWebApp && { borderColor: theme.successBackground },
    !!error && { borderColor: theme.errorBackground },
    borderless && { borderColor: 'transparent', borderWidth: 0 },
    borderWrapperStyle
  ]

  const inputWrapperStyles: ViewStyle[] = [
    styles.inputWrapper,
    {
      backgroundColor:
        themeType === THEME_TYPES.DARK
          ? theme.controlSurfaceBackground
          : theme.secondaryBackground,
      borderColor:
        themeType === THEME_TYPES.DARK ? theme.controlSurfaceBorder : theme.secondaryBorder
    },
    isFocused && !isMobileWebApp
      ? {
          borderColor: theme.primary
        }
      : {},
    isValid && !isMobileWebApp ? { borderColor: theme.successDecorative } : {},
    error ? { borderColor: theme.errorDecorative } : {},
    info ? { borderColor: theme.warningText } : {},
    disabled ? styles.disabled : {},
    borderless ? { borderColor: 'transparent', borderWidth: 0 } : {},
    ...(Array.isArray(inputWrapperStyle) ? inputWrapperStyle : [inputWrapperStyle || {}])
  ]

  const inputStyles = [styles.input, !!hasButton && spacings.pr0, inputStyle]

  return (
    <View style={[styles.inputContainer, containerStyle]}>
      {!!label && (
        <Text appearance="secondaryText" fontSize={14} weight="regular" style={styles.label}>
          {label}
          {!!tooltip && (
            <View style={{ width: 1 }}>
              <View style={{ position: 'absolute', top: -11.5, left: 8 }}>
                <InformationIcon
                  width={14}
                  height={14}
                  dataSet={{
                    tooltipId: tooltip.id,
                    tooltipContent: tooltip.content
                  }}
                />
              </View>
            </View>
          )}
        </Text>
      )}
      <View style={{ zIndex: 10 }}>
        <View style={borderWrapperStyles} ref={inputBorderWrapperRef}>
          <View style={inputWrapperStyles}>
            {!!leftIcon && <View style={[styles.leftIcon, leftIconStyle]}>{leftIcon()}</View>}
            {/* TextInput doesn't support border styles so we wrap it in a View */}
            <View style={[inputStyles, hasButton ? { width: '100%' } : {}]}>
              {customInputContent}
              <TextInput
                placeholderTextColor={theme.secondaryText}
                autoCapitalize="none"
                autoCorrect={false}
                editable={editable ?? !disabled}
                onBlur={handleOnBlur}
                onFocus={handleOnFocus}
                ref={setInputRef as any}
                accessibilityLabel={typeof label === 'string' ? label : undefined}
                {...a11yProps}
                {...(rest as any)}
                style={[
                  styles.nativeInput,
                  !!customInputContent && { position: 'absolute', zIndex: -1, opacity: 0 },
                  nativeInputStyle
                ]}
              />
            </View>
            {childrenBeforeButtons || null}
            {!!hasButton && (
              <AnimatedPressable
                // Previously `focusable={false}`, which put the in-field actions
                // ("Max", paste, reveal-seed) out of the keyboard tab flow entirely —
                // a screen-reader user could never reach them. They are real buttons,
                // so they belong in the flow; pass `accessibilityLabel` via
                // `buttonProps` when the button renders an icon rather than text.
                accessibilityRole="button"
                accessibilityLabel={typeof button === 'string' ? button : undefined}
                onPress={onButtonPress}
                disabled={disabled}
                style={[
                  styles.button,
                  buttonProps?.withBackground ? styles.buttonWithBackground : {},
                  buttonStyle,
                  animStyle
                ]}
                {...buttonProps}
                {...bindAnim}
              >
                {typeof button === 'string' || button instanceof String ? (
                  <Text weight="medium">{button}</Text>
                ) : (
                  button
                )}
              </AnimatedPressable>
            )}
          </View>
        </View>
        {childrenBelowInput}
      </View>
      {/* A stable live region: the id has to exist before the message does, or
          `aria-describedby` on the field would dangle, and swapping the node in
          and out is what stops assistive tech from noticing the change. */}
      <View
        nativeID={bottomLabelId}
        accessibilityLiveRegion="polite"
        style={styles.errorContainer}
      >
        {!!error && (
          <Text
            style={[styles.bottomLabel, bottomLabelStyle]}
            weight={isWeb ? 'regular' : undefined}
            fontSize={10}
            appearance={errorType === 'warning' ? 'warningText' : 'errorText'}
          >
            {error}
          </Text>
        )}

        {!!isValid && !!validLabel && !error && (
          <Text
            style={[styles.bottomLabel, bottomLabelStyle]}
            weight="regular"
            fontSize={12}
            appearance={validLabelAppearance || 'successText'}
          >
            {validLabel}
          </Text>
        )}

        {!!info && (
          <Text
            weight="regular"
            appearance="secondaryText"
            style={[styles.bottomLabel, bottomLabelStyle]}
            fontSize={10}
          >
            {info}
          </Text>
        )}
        {!!preventJumpOnValidationChange && !error && !isValid && !info && (
          <Text
            style={[styles.bottomLabel, bottomLabelStyle]}
            weight="regular"
            fontSize={10}
            // Purposefully render a space to keep the input height consistent
          >
            {' '}
          </Text>
        )}
        {renderConfirmAddress && renderConfirmAddress()}
      </View>
    </View>
  )
}

export default React.memo(Input)
